<template>
  <v-card rounded="xl" outlined :disabled="expirado">
    <v-card-title :class="{ 'pb-0': finalizado }">
      <v-row no-gutters>
        <v-col cols="12">
          <v-chip v-if="expirado" color="warning">Expirado</v-chip>
          <v-chip v-else class="primary--text" color="#F0F6FA">{{ tipo }}</v-chip>
        </v-col>
        <v-col class="mt-3 pb-2" :class="{ 'AgendamentoCard__border-dotted': !finalizado }">
          <h5>Exame {{ tipo }}</h5>
          <h5 class="font-weight-light">{{ nomeClinica }}</h5>
        </v-col>
      </v-row>
    </v-card-title>

    <template v-if="!finalizado">
      <v-card-text class="pb-1">
        <template v-if="online">
          <p>
            <v-icon class="mr-1" size="20" color="primary">mdi-account-voice</v-icon>
            <strong>Teleatendimento</strong>
          </p>
          <p>
            <v-icon class="mr-1" size="20" color="primary">mdi-doctor</v-icon>
            <strong>Médico:</strong> {{ medico }}
          </p>
          <p>
            <v-icon class="mr-1" size="20" color="primary">mdi-calendar</v-icon><strong>Data do Exame:</strong>
            {{ dtHorarioMedico }}
          </p>
          <p>
            <v-icon class="mr-1" size="20" color="primary">mdi-clock-outline</v-icon><strong>Horário:</strong>
            {{ horarioMedicoFormatado }} (Horário Local)
          </p>
          <div v-if="userCanEditHorario()">
            <v-icon class="mr-1" size="20" color="primary">mdi-pencil</v-icon>
            <router-link :to="`agendar-exame/${idAso}`">Alterar horário do exame</router-link>
          </div>
          <div v-if="fichaPreenchida && assinatura" class="text-center my-3">
            <AgendamentoCardButton
              :dtHorarioMedico="dtHorarioMedico"
              :horarioMedico="horarioMedicoFormatado"
              :idAsoMeetingRoom="idAsoMeetingRoom"
              :statusAsoMeetingRoom="statusAsoMeetingRoom"
              :datetimeHorarioMedico="datetimeHorarioMedico"
              :idAso="idAso"
            />
          </div>
        </template>
        <template v-else>
          <p>
            <v-icon class="mr-1" size="20" color="primary">mdi-calendar</v-icon><strong>Data do Exame:</strong>
            {{ dtAgendamento }}
          </p>
          <p>
            <v-icon class="mr-1" size="20" color="primary">mdi-clock-outline</v-icon><strong>Horário:</strong>
            {{ horario }}
          </p>
          <p>
            <v-icon class="mr-1" size="20" color="primary">mdi-map-marker</v-icon><strong>{{ endereco }}</strong>
          </p>
        </template>
        <div v-if="!disableActions" class="AgendamentoCard__border-dotted mt-3"></div>
      </v-card-text>
    </template>
    <AgendamentoCardActions
      v-if="!disableActions"
      :finalizado="finalizado"
      :arquivoRetorno="arquivoRetorno"
      :idAso="idAso"
      :idLinkAso="idLinkAso"
      :ficha-preenchida="fichaPreenchida"
      :ficha-clinica="fichaClinica"
      :statusValidacao="statusValidacao"
      :validacaoObrigatoria="validacaoObrigatoria"
      :assinatura="assinatura"
      @on-click="$emit('on-click')"
      @open-aso="$emit('open-aso')"
      @open-ficha-clinica="$emit('open-ficha-clinica')"
    />
    <RedirectAssinaturaDialog v-if="showRedirectAssinaturaDialog" v-model="showRedirectAssinaturaDialog" />
  </v-card>
</template>

<script>
import { formatDate } from '@/utils/formatter'
import { add, isBefore, subHours, format } from 'date-fns'
import RedirectAssinaturaDialog from '@/modules/Agendamento/dialogs/RedirectAssinaturaDialog'
import AgendamentoCardActions from '@/modules/Agendamento/AgendamentoCardActions'
import AgendamentoCardButton from '@/modules/Agendamento/AgendamentoCardButton'

export default {
  components: {
    AgendamentoCardActions,
    RedirectAssinaturaDialog,
    AgendamentoCardButton
  },
  props: {
    idAso: Number,
    idLinkAso: String,
    finalizado: Boolean,
    tipo: String,
    status: String,
    nomeClinica: String,
    dtAgendamento: String,
    horario: String,
    endereco: String,
    online: Boolean,
    arquivoRetorno: String,
    fichaClinica: String,
    fichaPreenchida: Boolean,
    medico: String,
    horarioMedico: String,
    link: String,
    dtHorarioMedico: String,
    assinatura: String,
    idHorario: Number,
    expirado: Boolean,
    disableActions: Boolean,
    idAsoMeetingRoom: String,
    statusAsoMeetingRoom: String,
    datetimeHorarioMedico: String,
    statusValidacao: String,
    validacaoObrigatoria: Boolean
  },
  data() {
    return {
      showRedirectAssinaturaDialog: false,
      loading: false
    }
  },
  computed: {
    plataformaUrl() {
      return process.env.VUE_APP_PLATAFORMA_URL
    },
    horarioMedicoFormatado() {
      if (!this.horarioMedico) return ''
      const horarios = this.horarioMedico.split(' às ')

      if (horarios.length === 2) {
        const inicio = this.formatHorarioLocal(horarios[0].trim())
        const fim = this.formatHorarioLocal(horarios[1].trim())
        return `${inicio} às ${fim}`
      } else {
        return this.formatHorarioLocal(this.horarioMedico)
      }
    }
  },
  methods: {
    formatHorarioLocal(timeString) {
      const date = new Date(`1970-01-01T${timeString}:00Z`)
      const result = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

      if (result && result !== 'Invalid Date') return result

       // Fallback UTC-3 (Brasília)
      const brt = subHours(date, 3)
      return format(brt, 'HH:mm')
    },
    userCanEditHorario() {
      const dataHoraExame = new Date(formatDate(this.dtHorarioMedico) + ' ' + this.horarioMedico)
      const dataHoraLimite = add(new Date(), { hours: 24 })
      return isBefore(dataHoraLimite, dataHoraExame)
    }
  },
  mounted() {
    this.userCanEditHorario()
  }
}
</script>

<style style="scoped">
.AgendamentoCard__border-dotted {
  border-bottom: 2px dotted #d6d6d6;
}
p {
  margin-bottom: 0.3em !important;
}
.btn-appointment {
  background-color: none !important;
  color: #006fee;
  text-decoration: underline;
  cursor: pointer;
  user-select: none;
  text-transform: unset !important;
  width: min-content !important;
  padding: 0px !important;
  font-size: 0.875rem !important;
  letter-spacing: unset !important;
}
</style>
